import React, {
  FC,
  useState,
  useCallback,
  memo,
  useEffect,
  useMemo,
} from 'react';
import { getActions, withGlobal } from '../../../global';

import type { ApiUserFullInfo, ApiUsername } from '../../../api/types';
import { ApiMediaFormat } from '../../../api/types';
import { EGender, ProfileEditProgress } from '../../../types';

import {
  PURCHASE_USERNAME,
  TME_LINK_PREFIX,
  USERNAME_REGEX,
} from '../../../config';
import { throttle } from '../../../util/schedulers';
import { selectTabState, selectUser } from '../../../global/selectors';
import { getChatAvatarHash } from '../../../global/helpers';
import { selectCurrentLimit } from '../../../global/selectors/limits';
import renderText from '../../common/helpers/renderText';
import useMedia from '../../../hooks/useMedia';
import AvatarEditable from '../../ui/AvatarEditable';
import SafeLink from '../../common/SafeLink';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import Input from '../../ui/Formik/Input';
import SelectDate from '../../ui/Formik/SelectDate/SelectDate';
import * as Yup from 'yup';
import TextArea from '../../ui/Formik/TextArea/TextArea';
import SelectCountry from '../../ui/Formik/SelectCountry/SelectCountry';
import SelectDropdown from '../../ui/Formik/SelectDropdown/SelectDropdown';
import Button from '../../ui/Button';
import IconSvg from '../../ui/IconSvg';
import useLastCallback from '../../../hooks/useLastCallback';

type StateProps = {
  currentAvatarHash?: string;
  currentFirstName?: string;
  currentLastName?: string;
  fullInfo?: ApiUserFullInfo;
  progress?: ProfileEditProgress;
  checkedUsername?: string;
  editUsernameError?: string;
  isUsernameAvailable?: boolean;
  maxBioLength: number;
  usernames?: ApiUsername[];
  isBusiness?: boolean;
  authErrorAvailable?: { email?: boolean; username?: boolean; error?: string };
};

interface IValues {
  first_name: string;
  last_name?: string;
  username: string;
  gender: string;
  birthday?: any;
  country_code: string;
  bio?: string;
}

const runThrottled = throttle((cb) => cb(), 60000, true);

const SettingsEditProfile: FC<StateProps> = ({
  currentAvatarHash,
  currentFirstName,
  currentLastName,
  progress,
  maxBioLength,
  usernames,
  fullInfo,
  isBusiness,
  authErrorAvailable,
}) => {
  const { loadCurrentUser, updateProfile } = getActions();

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.language) === 'rtl';

  const firstEditableUsername = useMemo(
    () => usernames?.find(({ isEditable }) => isEditable),
    [usernames]
  );
  const currentUsername = firstEditableUsername?.username || '';
  const {
    bio: currentBio,
    date,
    country,
    gender: currentGender,
  } = fullInfo || {};

  const [isUsernameTouched, setIsUsernameTouched] = useState(false);
  const [isProfileFieldsTouched, setIsProfileFieldsTouched] = useState(false);

  const [photo, setPhoto] = useState<File | undefined>();

  const currentAvatarBlobUrl = useMedia(
    currentAvatarHash,
    false,
    ApiMediaFormat.BlobUrl
  );

  const isLoading = progress === ProfileEditProgress.InProgress;

  // Due to the parent Transition, this component never gets unmounted,
  // that's why we use throttled API call on every update.
  useEffect(() => {
    runThrottled(() => {
      loadCurrentUser();
    });
  }, [loadCurrentUser]);

  useEffect(() => {
    setPhoto(undefined);
  }, [currentAvatarBlobUrl]);

  useEffect(() => {
    if (progress === ProfileEditProgress.Complete) {
      setIsProfileFieldsTouched(false);
      setIsUsernameTouched(false);
    }
  }, [progress]);

  const handlePhotoChange = useCallback((photo: File) => {
    updateProfile({
      photo,
    });
  }, []);

  const handleProfileSave = useCallback(
    (values: IValues) => {
      updateProfile({
        ...values,
        birthday: isBusiness
          ? undefined
          : values.birthday.toISOString().split('.')[0] + '+0000',
      });
    },
    [photo, isProfileFieldsTouched, isUsernameTouched, updateProfile]
  );

  function renderPurchaseLink() {
    const purchaseInfoLink = `${TME_LINK_PREFIX}${PURCHASE_USERNAME}`;

    return (
      <p className='settings-item-description' dir={isRtl ? 'rtl' : undefined}>
        {(t('lng_username_purchase_available') as string)
          .replace('{link}', '%PURCHASE_LINK%')
          .split('%')
          .map((s) => {
            return s === 'PURCHASE_LINK' ? (
              <SafeLink url={purchaseInfoLink} text={`@${PURCHASE_USERNAME}`} />
            ) : (
              s
            );
          })}
      </p>
    );
  }

  const toDate = Date.now();

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .test(
        'start_number',
        'Sorry, a username can`t start with a number.',
        (val) => isNaN(Number(val![0]))
      )
      .matches(/^(?=.*?[a-z]).{1,}$/, t('Validation.username_validation'))
      .required(t('Validation.required_username'))
      .min(5, t('Validation.username_min_length'))
      .max(32, t('Validation.username_max_length')),
    first_name: Yup.string()
      .required(t('Validation.required_firstname'))
      .min(1, t('Validation.min_length', { count: 1 }))
      .max(64, t('Validation.max_length', { count: 64 })),
    last_name: Yup.string()
      .min(1, t('Validation.min_length', { count: 1 }))
      .max(64, t('Validation.max_length', { count: 64 })),
    bio: Yup.string().max(70, t('Validation.max_length', { count: 70 })),
  });

  const initialValue = {
    first_name: currentFirstName || '',
    last_name: currentLastName || '',
    username: currentUsername,
    gender: currentGender || '',
    birthday: date && new Date(date * 1000),
    country_code: country || '',
    bio: currentBio || '',
  };

  const validateUsername = useMemo(() => {
    let errorText = '';
    if (!authErrorAvailable) return errorText;
    const { error, username } = authErrorAvailable || {};
    if (username) return errorText;
    if (error !== 'USERNAME_INVALID') {
      errorText = error || t('Errors.ThisUsernameUnavailable');
    }

    return errorText;
  }, [authErrorAvailable]);

  return (
    <div className='settings-container'>
      <div className='settings-edit-profile'>
        <AvatarEditable
          currentAvatarBlobUrl={currentAvatarBlobUrl}
          onChange={handlePhotoChange}
          disabled={isLoading}
        />
        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            handleProfileSave(values);
            setPhoto(undefined);
            resetForm({ values });
            setSubmitting(false);
          }}
        >
          {({ values, isValid, isSubmitting, dirty }) => {
            return (
              <Form>
                <div className='input-group'>
                  <Field
                    name='first_name'
                    component={Input}
                    label={t('FirstName')}
                    maxLength={64}
                    withoutCleaning
                  />
                  {!isBusiness && (
                    <Field
                      name='last_name'
                      component={Input}
                      label={t('LastName')}
                      maxLength={64}
                      withoutCleaning
                    />
                  )}
                </div>
                <p
                  className='settings-item-description'
                  dir={isRtl ? 'rtl' : undefined}
                >
                  {renderText(String(t('Registration.name_description')), [
                    'br',
                    'simple_markdown',
                  ])}
                </p>
                <div className='input-group mb-2'>
                  <Field
                    name='username'
                    component={Input}
                    label={t('Registration.place_username')}
                    withoutCleaning
                    iconLeft={<IconSvg name='mail' />}
                    size='smaller'
                    maxLength={32}
                    regexp={USERNAME_REGEX}
                    errorText={validateUsername}
                    isValidated
                  />

                  {!isBusiness && (
                    <Field
                      name='birthday'
                      label='Date of birth'
                      component={SelectDate}
                      maxAt={toDate}
                      size='smaller'
                    />
                  )}
                </div>
                <Field
                  name='bio'
                  component={TextArea}
                  rows='4'
                  maxLength={maxBioLength}
                  maxLengthIndicator={
                    maxBioLength
                      ? (maxBioLength - values.bio.length).toString()
                      : undefined
                  }
                />
                <p
                  className='settings-item-description'
                  dir={isRtl ? 'rtl' : undefined}
                >
                  {renderText(String(t('Registration.bio_description')), [
                    'br',
                    'simple_markdown',
                  ])}
                </p>
                <div className='input-group mb-6'>
                  <Field
                    scrollId='settings_content'
                    name='country_code'
                    component={SelectCountry}
                    positionY='bottom'
                  />
                  {!isBusiness && (
                    <Field
                      scrollId='settings_content'
                      name='gender'
                      label={t('Registration.label_gender')}
                      component={SelectDropdown}
                      dataList={EGender}
                    />
                  )}
                </div>
                <div className='form-submit'>
                  <Button
                    type='submit'
                    size='smaller'
                    isShiny
                    disabled={!dirty || !isValid || isSubmitting}
                  >
                    {t('Save')}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      {/* <FloatingActionButton
        isShown={isSaveButtonShown}
        onClick={handleProfileSave}
        disabled={isLoading}
        ariaLabel={lang('Save')}
      >
        {isLoading ? <Spinner color='white' /> : <i className='icon-check' />}
      </FloatingActionButton> */}
    </div>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { currentUserId, authErrorAvailable } = global;
    const {
      progress,
      isUsernameAvailable,
      checkedUsername,
      error: editUsernameError,
    } = selectTabState(global).profileEdit || {};
    const currentUser = currentUserId
      ? selectUser(global, currentUserId)
      : undefined;

    const maxBioLength = selectCurrentLimit(global, 'aboutLength');

    if (!currentUser) {
      return {
        progress,
        checkedUsername,
        isUsernameAvailable,
        editUsernameError,
        maxBioLength,
        authErrorAvailable,
      };
    }

    const {
      firstName: currentFirstName,
      lastName: currentLastName,
      usernames,
      isBusiness,
      fullInfo,
    } = currentUser;
    const currentAvatarHash = getChatAvatarHash(currentUser);

    return {
      currentAvatarHash,
      currentFirstName,
      currentLastName,
      fullInfo,
      progress,
      isUsernameAvailable,
      checkedUsername,
      editUsernameError,
      maxBioLength,
      usernames,
      isBusiness,
      authErrorAvailable,
    };
  })(SettingsEditProfile)
);
